// THE PORTAL PROJECTION VOCABULARY — one table, three consumers.
//
// The portal is a projection: a nav entry only appears when the publication matrix says the
// client may see that object type, so the client never clicks into a wall. That rule was
// stated in two places — the portal SPA's own `VIEWS` array and portal-admin's preview of what
// the client would see — and two copies of a projection rule is how the preview starts lying.
//
// It cannot live in src/server/portal/policy.ts (FEASIBILITY BL-4): that module imports `pool`
// → `pg` → `net`/`tls`/`fs`, and esbuild bundles src/portal/index.tsx for the BROWSER with no
// platform/external configuration, so importing it from an SPA fails the build outright.
// It lives here, next to src/shared/contact.ts, for exactly the reason that file states:
// "shared by the server and both SPAs so the two sides can never disagree".
//
// NOTHING here may import `pg`, React, or anything from src/server.

// ───────────────────────── the vocabulary ─────────────────────────

/** publication_settings.object_type CHECK list — keep in sync with schema.sql. */
export const PORTAL_OBJECT_TYPES = [
  'requirements',
  'deliverables',
  'acceptance_criteria',
  'test_cases',
  'documents',
  'milestones',
  'change_requests',
  'defects',
  'sows',
  'acceptance_records',
] as const;

export type PortalObjectType = (typeof PORTAL_OBJECT_TYPES)[number];

/** hidden → the type does not exist for this client. read → visible. read_act → and mutable. */
export type Visibility = 'hidden' | 'read' | 'read_act';

export const VISIBILITIES: Visibility[] = ['hidden', 'read', 'read_act'];

export const isPortalObjectType = (v: unknown): v is PortalObjectType =>
  typeof v === 'string' && (PORTAL_OBJECT_TYPES as readonly string[]).includes(v);

export const isVisibility = (v: unknown): v is Visibility =>
  typeof v === 'string' && VISIBILITIES.includes(v as Visibility);

/**
 * Object types whose rows carry their own `published` flag. `defects` is deliberately absent —
 * the schema has no defects.published column, so object-type visibility is the only gate for
 * defects (a schema gap, not a policy hole: defects are raised BY the client, so hiding an
 * individual one from its author has no meaning).
 */
export const OBJECT_TYPES_WITH_PUBLISHED_FLAG: PortalObjectType[] = [
  'requirements', 'deliverables', 'acceptance_criteria', 'test_cases',
  'documents', 'milestones', 'change_requests', 'sows', 'acceptance_records',
];

// ───────────────────────── the view table ─────────────────────────

export type PortalViewId =
  | 'overview' | 'requirements' | 'documents' | 'sow' | 'uat' | 'change-requests' | 'acceptance';

export interface PortalView {
  id: PortalViewId;
  label: string;
  /** The object type whose visibility gates this view. Absent = always present. */
  needs?: PortalObjectType;
  /** The view offers at least one mutation, which `read_act` gates. */
  act?: boolean;
  /**
   * That mutation is SPONSOR-ONLY — a reviewer sees the view and is refused the act.
   * Verified against src/server/portal/routes.ts: sign a SOW version (:1487), decide a change
   * request (:1601), sign an acceptance record (:2115) — those three and no others.
   */
  sponsorOnly?: boolean;
}

/**
 * The order is the nav order. Overview is unconditional: it is the one surface that can
 * honestly say "nothing has been published to you yet".
 */
export const PORTAL_VIEWS: PortalView[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'requirements', label: 'Requirements', needs: 'requirements', act: true },
  { id: 'documents', label: 'Documents', needs: 'documents', act: true },
  { id: 'sow', label: 'Statement of work', needs: 'sows', act: true, sponsorOnly: true },
  { id: 'uat', label: 'UAT', needs: 'test_cases', act: true },
  { id: 'change-requests', label: 'Change requests', needs: 'change_requests', act: true, sponsorOnly: true },
  { id: 'acceptance', label: 'Acceptance', needs: 'acceptance_records', act: true, sponsorOnly: true },
];

// ───────────────────── what each object type is worth, in the portal ─────────────────────
//
// ADDITIVE (portal-admin D12 / §3.1). The operator page used to carry a hand-written
// `ACT_MEANS` constant, and one of its ten entries was FALSE: `deliverables: 'comment'`
// promised a surface that does not exist — portal/routes.ts:1269 has the endpoint, but
// PORTAL_VIEWS has no deliverables view and the comment UI is mounted only on requirements
// and acceptance criteria. A console that states what the client can do has to read it from
// the same table the portal renders from, or it is guessing.
//
// `view: null` means the type has NO tab of its own: it surfaces inside another view, as a
// chip or a nested list, and its visibility gates that disclosure rather than a navigation
// entry. That is a real answer, not a missing one.

export interface PortalSurface {
  /** The operator-facing name of the object type. */
  label: string;
  /** The view that LISTS this type, or null when it only ever surfaces inside another. */
  view: PortalViewId | null;
  /** What `read` gets the client. Always a sentence — every type is reachable somehow. */
  read: string;
  /** What `read_act` additionally unlocks, or null when the portal offers no act at all. */
  act: string | null;
  /** That act is sponsor-only (DESIGN §3, enforced in portal/routes.ts). */
  sponsorOnly?: boolean;
  /** True when rows of this type carry their own `published` flag. */
  rowFlag: boolean;
}

export const PORTAL_SURFACES: Record<PortalObjectType, PortalSurface> = {
  requirements: {
    label: 'Requirements', view: 'requirements', rowFlag: true,
    read: 'read the statements', act: 'comment · propose a change',
  },
  acceptance_criteria: {
    label: 'Acceptance criteria', view: null, rowFlag: true,
    read: 'read the criteria, nested under the requirement they test', act: 'comment',
  },
  deliverables: {
    label: 'Deliverables', view: null, rowFlag: true,
    // The correction. There is no deliverables view and no deliverables comment surface.
    read: 'named on other objects; no client view lists these', act: null,
  },
  documents: {
    label: 'Documents', view: 'documents', rowFlag: true,
    read: 'read · download', act: 'download · upload new',
  },
  test_cases: {
    label: 'Test cases', view: 'uat', rowFlag: true,
    read: 'read the cases', act: 'record pass / fail / blocked',
  },
  milestones: {
    label: 'Milestones', view: null, rowFlag: true,
    read: 'named on other objects; no client view lists these', act: null,
  },
  sows: {
    label: 'SOW versions', view: 'sow', rowFlag: true,
    read: 'read · download', act: 'sponsor signs → creates the baseline', sponsorOnly: true,
  },
  change_requests: {
    label: 'Change requests', view: 'change-requests', rowFlag: true,
    read: 'read the requests', act: 'sponsor approves or rejects', sponsorOnly: true,
  },
  acceptance_records: {
    label: 'Acceptance records', view: 'acceptance', rowFlag: true,
    read: 'read the record', act: 'sponsor signs', sponsorOnly: true,
  },
  defects: {
    // No `published` column exists, so object-type visibility is the whole decision.
    label: 'Defects', view: null, rowFlag: false,
    read: 'read the defects they raised', act: 'raise a defect',
  },
};

/**
 * The capability sentence for one view under one matrix — the string portal-admin prints and
 * the portal enforces. `read_act` on a sponsor-only act is stated as such: a reviewer sees the
 * view and is refused the act, and the console must not pretend otherwise.
 */
export function viewCapability(view: PortalView, visibility: VisibilityMap): string {
  if (!view.needs) return 'the summary of everything below';
  const surface = PORTAL_SURFACES[view.needs];
  const vis = visibility[view.needs];
  if (vis === 'read_act' && surface.act) {
    return surface.sponsorOnly ? `${surface.act} (sponsor only)` : surface.act;
  }
  return surface.read;
}

/** The visibility map as the portal session ships it: every object type, defaulting to hidden. */
export type VisibilityMap = Partial<Record<string, Visibility | string | undefined>>;

/**
 * THE projection rule, stated once. The portal SPA renders exactly this list; portal-admin's
 * preview computes exactly this list from the same map, so "what the client sees" is one answer.
 */
export function visiblePortalViews(visibility: VisibilityMap): PortalView[] {
  return PORTAL_VIEWS.filter(
    (v) => !v.needs || (visibility[v.needs] !== undefined && visibility[v.needs] !== 'hidden'),
  );
}

/** Can this session act in this view? Preview sessions can never act, whatever the matrix says. */
export function canActInView(
  view: PortalView,
  visibility: VisibilityMap,
  session: { role?: 'sponsor' | 'reviewer' | null; preview?: boolean },
): boolean {
  if (!view.act || session.preview) return false;
  if (view.sponsorOnly && session.role !== 'sponsor') return false;
  const need = view.needs;
  if (!need) return false;
  return visibility[need] === 'read_act';
}
